import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar, Trophy, Eye, Loader2, ExternalLink, EyeOff } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface FollowedItem {
  id: string;
  entity_type: "event" | "challenge";
  entity_id: string;
  created_at: string;
  entity_name?: string;
  entity_date?: string;
  entity_status?: string;
}

const UserFollowing: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") === "challenges" ? "challenges" : "events";

  const { data: follows, isLoading, error } = useQuery({
    queryKey: ["user-follows"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: followsData, error: followsError } = await supabase
        .from("user_follows")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (followsError) throw followsError;

      // Enrich with entity details
      const enriched: FollowedItem[] = [];

      for (const follow of followsData || []) {
        let entityName = "Unknown";
        let entityDate: string | undefined;
        let entityStatus: string | undefined;

        if (follow.entity_type === "event") {
          const { data: event } = await supabase
            .from("events")
            .select("name, event_date")
            .eq("id", follow.entity_id)
            .maybeSingle();
          
          if (event) {
            entityName = event.name;
            entityDate = event.event_date;
          }
        } else if (follow.entity_type === "challenge") {
          const { data: challenge } = await supabase
            .from("challenges")
            .select("title, status")
            .eq("id", follow.entity_id)
            .maybeSingle();
          
          if (challenge) {
            entityName = challenge.title;
            entityStatus = challenge.status || undefined;
          }
        }

        enriched.push({
          id: follow.id,
          entity_type: follow.entity_type as "event" | "challenge",
          entity_id: follow.entity_id,
          created_at: follow.created_at || "",
          entity_name: entityName,
          entity_date: entityDate,
          entity_status: entityStatus,
        });
      }

      return enriched;
    },
  });

  const unfollow = useMutation({
    mutationFn: async (followId: string) => {
      const { error } = await supabase
        .from("user_follows")
        .delete()
        .eq("id", followId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-follows"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Unfollowed successfully");
    },
    onError: () => {
      toast.error("Failed to unfollow");
    },
  });

  const eventFollows = follows?.filter(f => f.entity_type === "event") || [];
  const challengeFollows = follows?.filter(f => f.entity_type === "challenge") || [];

  if (isLoading) {
    return (
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-8">
        <Card className="border-destructive">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load your followed items. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderFollowCard = (item: FollowedItem) => {
    const isEvent = item.entity_type === "event";
    const Icon = isEvent ? Calendar : Trophy;
    const viewLink = isEvent ? "/events" : "/challenges";

    return (
      <Card key={item.id} className="hover:border-primary/50 transition-colors">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon className={`h-5 w-5 ${isEvent ? "text-blue-500" : "text-orange-500"}`} />
              <CardTitle className="text-lg">{item.entity_name}</CardTitle>
            </div>
            {item.entity_status && (
              <Badge variant="outline">{item.entity_status}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {item.entity_date && (
              <p>Date: {format(new Date(item.entity_date), "MMMM d, yyyy")}</p>
            )}
            <p>Following since {format(new Date(item.created_at), "MMM d, yyyy")}</p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => unfollow.mutate(item.id)}
              disabled={unfollow.isPending}
            >
              <EyeOff className="h-4 w-4" />
              <span>Unfollow</span>
            </Button>
            <Button variant="default" size="sm" asChild>
              <Link to={viewLink}>
                {isEvent ? "Register" : "Join Team"}
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to={viewLink}>
                <ExternalLink className="h-4 w-4" />
                <span>View</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Following</h1>
        <p className="text-muted-foreground mt-2">
          Events and challenges you're following. You'll receive updates about these.
        </p>
      </div>

      {(follows?.length || 0) === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Eye className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <CardTitle className="mb-2">Not Following Anything Yet</CardTitle>
            <CardDescription className="mb-4">
              Follow events or challenges to stay updated without committing to register or join.
            </CardDescription>
            <div className="flex items-center justify-center gap-2">
              <Button asChild variant="outline">
                <Link to="/events">Browse Events</Link>
              </Button>
              <Button asChild>
                <Link to="/challenges">Browse Challenges</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Events ({eventFollows.length})
            </TabsTrigger>
            <TabsTrigger value="challenges" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Challenges ({challengeFollows.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="space-y-4">
            {eventFollows.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Calendar className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-3">You're not following any events.</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/events">Browse Events</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {eventFollows.map(renderFollowCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="challenges" className="space-y-4">
            {challengeFollows.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-3">You're not following any challenges.</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/challenges">Browse Challenges</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {challengeFollows.map(renderFollowCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default UserFollowing;
